import { Fragment, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';

import TimeAgo from 'react-timeago';

import formatName from '../modules/format-name';
import libravatarImage from '../modules/libravatar-image.mjs';
import { usePinnedPeople } from '../modules/pinnedPeople';
import FloorMap from './FloorMap';

const identityReplacements = {
    'madeleine.moller': 'madeleine.moller-stahl',
};

const sortByIdentity = ( a, b ) => {
    return a.identity.localeCompare( b.identity );
};

// Room numbers encode the floor as their first digit (e.g. "101", "314",
// "601" → floors 1, 3, 6) — this matches how room identifiers are read in
// Rooms.jsx (getRoomFloor), so a person's floor and a room's floor agree.
const getFloorNumber = ( device ) => {
    return device.ap.location.charAt( 0 );
};

const sortByFloor = ( a, b ) => {
    const floorDiff = parseInt( getFloorNumber( a ), 10 ) - parseInt( getFloorNumber( b ), 10 );

    if ( floorDiff !== 0 ) {
        return floorDiff;
    }

    return a.identity.localeCompare( b.identity );
};

const FLIP_DURATION = 320;

// Repel effect tuning — kept subtle on purpose: a small nudge, not a bounce.
// Radius is large enough to sweep across several cards in the grid at once,
// with a quadratic falloff so distant cards barely move while close ones do.
const REPEL_RADIUS = 320;
const REPEL_STRENGTH = 4;

const FloorCard = function({floorNumber, onClick}){
    return ( <div
        className = { 'device-wrapper floor-card' }
        onClick = { onClick }
        role = { 'button' }
        tabIndex = { 0 }
        title = { `Visa planritning för våning ${ floorNumber }` }
    >
        <div
            className = { 'device-photo-frame' }
        >
            <div
                className = { 'device-photo floor-card-photo' }
            >
                <span
                    className = { 'floor-card-number' }
                >
                    { floorNumber }
                </span>
            </div>
        </div>
    </div> );
};

const HeartIcon = function({filled}){
    return ( <svg
        viewBox = { '0 0 24 24' }
        width = { 20 }
        height = { 20 }
        fill = { filled ? 'currentColor' : 'none' }
        stroke = { 'currentColor' }
        strokeWidth = { 2 }
        strokeLinecap = { 'round' }
        strokeLinejoin = { 'round' }
        aria-hidden = { 'true' }
    >
        <path d = { 'M19 14c1.49-1.46 3-3.21 3-5.5A5.5 5.5 0 0 0 16.5 3c-1.76 0-3 .5-4.5 2-1.5-1.5-2.74-2-4.5-2A5.5 5.5 0 0 0 2 8.5c0 2.3 1.5 4.05 3 5.5l7 7Z' } />
    </svg> );
};

const DeviceCard = function({device, urlParams, cardRef, repelRef, displayNames, isPinned, onTogglePin}){
    if ( device.identity in identityReplacements ) {
        device.identity = identityReplacements[ device.identity ];
    }

    const pinLabel = isPinned ? 'Sluta pinna kollega' : 'Pinna kollega';

    return ( <div
        className = { 'device-wrapper' }
        title = { device.identity }
        data-identity = { device.identity }
        ref = { cardRef }
    >
        <div
            className = { 'device-photo-frame' }
            ref = { repelRef }
        >
            <div
                className = { 'device-photo' }
            >
                <img
                    alt = { device.identity }
                    src = { libravatarImage(`${ device.identity }@stendahls.se`) }
                />
            </div>
            <button
                type = { 'button' }
                className = { `pin-button${ isPinned ? ' pinned' : '' }` }
                onClick = { () => onTogglePin( device.identity ) }
                aria-pressed = { isPinned }
                aria-label = { pinLabel }
                title = { pinLabel }
            >
                <HeartIcon
                    filled = { isPinned }
                />
            </button>
            <div
                className = { 'device-name-tag' }
            >
                <span
                    className = { 'device-name' }
                >
                    { displayNames?.get( device.identity ) ?? formatName( device.identity ) }
                </span>
                { device.locator &&
                    <span
                        className = { 'device-room' }
                    >
                        { device.ap.location }
                    </span>
                }
            </div>
        </div>
        { urlParams.get( 'debug' ) &&
            <div
                className = { 'last-seen-wrapper' }
            >
                <div
                    className = { 'location-wrapper' }
                >
                    { device.hostname }
                </div>
                { device.locator &&
                    <div
                        className = { 'location-wrapper' }
                    >
                        <div>
                            { device.mac }
                        </div>
                    </div>
                }
                <TimeAgo
                    date = { device.lastSeen * 1000 }
                />
            </div>
        }
    </div> );
};

// With a ?people= filter the tab switch swaps the tree (grouped <-> flat), so
// Peeps remounts and loses its previous card positions. A remount hands the
// positions over through here; only handoffs from the same commit are used.
const HANDOFF_MAX_AGE = 100;
const rectHandoff = { rects: new Map(), time: 0 };

const Peeps = function({validDevices, urlParams, sortMode, displayNames}){
    const { pinned, togglePin } = usePinnedPeople();
    const rootRef = useRef( null );
    const nodeRefs = useRef( new Map() );
    const previousRects = useRef( new Map() );
    const repelNodeRefs = useRef( new Map() );
    const [ openFloorNumber, setOpenFloorNumber ] = useState( null );

    const sortedDevices = useMemo( () => {
        const sorter = sortMode === 'floor' ? sortByFloor : sortByIdentity;

        return [ ...validDevices ].sort( sorter );
    }, [ validDevices, sortMode ] );

    // In the name view, favourites get a row of their own above everyone else;
    // the rest keep their normal order. (The floor view keeps its floor
    // grouping, so favourites aren't split out there.)
    const { favouriteDevices, otherDevices } = useMemo( () => {
        if ( sortMode === 'floor' ) {
            return { favouriteDevices: [], otherDevices: sortedDevices };
        }

        return {
            favouriteDevices: sortedDevices.filter( ( device ) => pinned.has( device.identity ) ),
            otherDevices: sortedDevices.filter( ( device ) => !pinned.has( device.identity ) ),
        };
    }, [ sortedDevices, sortMode, pinned ] );

    const floorGroups = useMemo( () => {
        if ( sortMode !== 'floor' ) {
            return null;
        }

        const groups = new Map();

        sortedDevices.forEach( ( device ) => {
            const floorNumber = getFloorNumber( device );

            if ( !groups.has( floorNumber ) ) {
                groups.set( floorNumber, [] );
            }

            groups.get( floorNumber ).push( device );
        } );

        return [ ...groups.entries() ].sort( ( a, b ) => {
            return parseFloat( a[ 0 ] ) - parseFloat( b[ 0 ] );
        } );
    }, [ sortedDevices, sortMode ] );

    useLayoutEffect( () => {
        return () => {
            const now = performance.now();

            if ( now - rectHandoff.time > HANDOFF_MAX_AGE ) {
                rectHandoff.rects = new Map();
            }

            previousRects.current.forEach( ( rect, mac ) => {
                rectHandoff.rects.set( mac, rect );
            } );
            rectHandoff.time = now;
        };
    }, [] );

    useLayoutEffect( () => {
        const nextRects = new Map();

        if ( previousRects.current.size === 0
            && performance.now() - rectHandoff.time <= HANDOFF_MAX_AGE ) {
            previousRects.current = rectHandoff.rects;
        }

        nodeRefs.current.forEach( ( node, mac ) => {
            if ( !node ) {
                return;
            }

            nextRects.set( mac, node.getBoundingClientRect() );
        } );

        nextRects.forEach( ( nextRect, mac ) => {
            const previousRect = previousRects.current.get( mac );
            const node = nodeRefs.current.get( mac );

            if ( !previousRect || !node ) {
                return;
            }

            const deltaX = previousRect.left - nextRect.left;
            const deltaY = previousRect.top - nextRect.top;

            if ( deltaX === 0 && deltaY === 0 ) {
                return;
            }

            node.style.transition = 'none';
            node.style.transform = `translate(${ deltaX }px, ${ deltaY }px)`;

            requestAnimationFrame( () => {
                node.style.transition = `transform ${ FLIP_DURATION }ms cubic-bezier(0.22, 1, 0.36, 1)`;
                node.style.transform = '';
            } );
        } );

        previousRects.current = nextRects;
    }, [ sortedDevices, favouriteDevices ] );

    useEffect( () => {
        const root = rootRef.current;

        if ( !root ) {
            return;
        }

        let frameRequested = false;
        let lastEvent = null;

        const applyRepel = () => {
            frameRequested = false;

            repelNodeRefs.current.forEach( ( node ) => {
                if ( !node ) {
                    return;
                }

                if ( !lastEvent ) {
                    node.style.transform = '';

                    return;
                }

                const rect = node.getBoundingClientRect();
                const centerX = rect.left + rect.width / 2;
                const centerY = rect.top + rect.height / 2;

                const dx = centerX - lastEvent.clientX;
                const dy = centerY - lastEvent.clientY;
                const distance = Math.sqrt( dx * dx + dy * dy );

                if ( distance >= REPEL_RADIUS || distance === 0 ) {
                    node.style.transform = '';

                    return;
                }

                const linearFalloff = 1 - distance / REPEL_RADIUS;
                const falloff = linearFalloff * linearFalloff;
                const pushX = ( dx / distance ) * REPEL_STRENGTH * falloff;
                const pushY = ( dy / distance ) * REPEL_STRENGTH * falloff;

                node.style.transform = `translate(${ pushX }px, ${ pushY }px)`;
            } );
        };

        const handleMouseMove = ( event ) => {
            lastEvent = event;

            if ( !frameRequested ) {
                frameRequested = true;
                requestAnimationFrame( applyRepel );
            }
        };

        const handleMouseLeave = () => {
            lastEvent = null;

            if ( !frameRequested ) {
                frameRequested = true;
                requestAnimationFrame( applyRepel );
            }
        };

        root.addEventListener( 'mousemove', handleMouseMove );
        root.addEventListener( 'mouseleave', handleMouseLeave );

        return () => {
            root.removeEventListener( 'mousemove', handleMouseMove );
            root.removeEventListener( 'mouseleave', handleMouseLeave );
        };
    }, [] );

    const makeCardRef = ( device ) => {
        return ( node ) => {
            if ( node ) {
                nodeRefs.current.set( device.mac, node );
            } else {
                nodeRefs.current.delete( device.mac );
            }
        };
    };

    const makeRepelRef = ( device ) => {
        return ( node ) => {
            if ( node ) {
                repelNodeRefs.current.set( device.mac, node );
            } else {
                repelNodeRefs.current.delete( device.mac );
            }
        };
    };

    if ( floorGroups ) {
        return ( <div
            className = { 'peeps-root' }
            ref = { rootRef }
        >
            { floorGroups.map( ( [ floorNumber, devices ] ) => {
                return ( <div
                    className = { 'floor-section' }
                    key = { floorNumber }
                >
                    <div
                        className = { 'devices-wrapper' }
                    >
                        <FloorCard
                            floorNumber = { floorNumber }
                            onClick = { () => setOpenFloorNumber( floorNumber ) }
                        />
                        { devices.map( ( device ) => {
                            return <DeviceCard
                                key = { device.mac }
                                device = { device }
                                urlParams = { urlParams }
                                cardRef = { makeCardRef( device ) }
                                repelRef = { makeRepelRef( device ) }
                                displayNames = { displayNames }
                                isPinned = { pinned.has( device.identity ) }
                                onTogglePin = { togglePin }
                            />;
                        } ) }
                    </div>
                </div> );
            } ) }
            { openFloorNumber &&
                <div
                    className = { 'floor-map-overlay' }
                    onClick = { () => setOpenFloorNumber( null ) }
                >
                    <div
                        className = { 'floor-map-overlay-content' }
                        onClick = { ( event ) => event.stopPropagation() }
                    >
                        <button
                            type = { 'button' }
                            className = { 'floor-map-overlay-close' }
                            onClick = { () => setOpenFloorNumber( null ) }
                            aria-label = { 'Stäng' }
                        >
                            ×
                        </button>
                        <h2
                            className = { 'floor-map-overlay-title' }
                        >
                            { `Våning ${ openFloorNumber }` }
                        </h2>
                        <FloorMap
                            floorNumber = { openFloorNumber }
                            validDevices = { validDevices }
                            displayNames = { displayNames }
                        />
                    </div>
                </div>
            }
        </div> );
    }

    return ( <div
        className = { 'peeps-root' }
        ref = { rootRef }
    >
        <div
            className = { 'devices-wrapper' }
        >
            { [ ...favouriteDevices, ...otherDevices ].map( ( device, index ) => {
                // One shared wrapper (instead of one per group) so a card keeps
                // its DOM node when it's favourited — that is what lets the
                // FLIP animation slide it into its new place.
                const startsOthers = favouriteDevices.length > 0
                    && index === favouriteDevices.length;

                return ( <Fragment
                    key = { device.mac }
                >
                    { startsOthers &&
                        <div
                            className = { 'favourites-divider' }
                            aria-hidden = { 'true' }
                        />
                    }
                    <DeviceCard
                        device = { device }
                        urlParams = { urlParams }
                        cardRef = { makeCardRef( device ) }
                        repelRef = { makeRepelRef( device ) }
                        displayNames = { displayNames }
                        isPinned = { pinned.has( device.identity ) }
                        onTogglePin = { togglePin }
                    />
                </Fragment> );
            } ) }
        </div>
    </div> );
}

export default Peeps;
